/**
 * /stylus-test — сборка страницы.
 *
 * Здесь только оркестровка: подписки, отрисовка холстов, переключение вкладок
 * и вывод чисел. Вся математика — в metrics.js, все выводы — в diagnose.js,
 * весь захват — в capture.js. Так тест на реальном пере может дёргать
 * измерения без DOM, а страница остаётся читаемой.
 *
 * Осознанное решение по UX: НЕТ мастера с кнопкой «начать шаг 2». Все три
 * замера нажима (порог, разгон, дрожание удержания) считаются непрерывно из
 * того, что человек и так делает — шаги в списке просто зажигаются, когда
 * нужные данные набрались. Мастер с кнопками ломается ровно там, где человек
 * сделал движение чуть иначе, чем задумал автор, и вынуждает переигрывать.
 */
import { attach, caps, observed, session, resetSession, environment } from './capture.js';
import {
    effectivePressureLevels, activationForce, reportRate, wobble,
    pressureRamp, holdJitter, latency, tiltToSpherical, median, stdev, pressureSpikes
} from './metrics.js';
import { diagnose, supportText } from './diagnose.js';
import { drawReportCard, downloadJson, copyText } from './report.js';

const cfgEl = document.getElementById('stlCfg');
const CFG = cfgEl ? JSON.parse(cfgEl.textContent) : { t: {}, diag: {} };
const T = CFG.t || {};
const DIAG = CFG.diag || {};
const SLUG = 'stylus-test';
const $ = (id) => document.getElementById(id);

const env = environment();

/* ───────────────────────── телеметрия ─────────────────────────
   Вопрос, ради которого она здесь: у скольких пришедших перо ДЕЙСТВИТЕЛЬНО
   сломано, а сколько просто зашли посмотреть с мышью. Без этого нельзя ни
   оценить тул, ни понять, какие подсказки писать первыми.

   Что шлём:
   • стадии воронки — open → first_input(тип) → pen_detected → measured →
     verdict_<статус>; `dwell_ms` считает сам примитив;
   • каждый ПЛОХОЙ вердикт один раз как tool_diagnostic — это и есть ответ
     «Windows Ink выключен» против «планшета нет вовсе»;
   • tool_complete в момент, когда вердикт стал осмысленным (для этого тула
     выданный результат — это диагноз, а не файл).
   Слаг берётся из <meta name="tool-slug">, передавать не нужно. */
const _stages = Object.create(null);
function stage(name, meta) {
    if (_stages[name]) return;
    _stages[name] = 1;
    if (window.__stage) { try { window.__stage(name, meta); } catch (_) {} }
}
/** Стадии, которые обязаны повторяться (навигация) — без дедупа по имени. */
function stageRepeat(name, meta) {
    if (window.__stage) { try { window.__stage(name, meta); } catch (_) {} }
}
const _diagSent = Object.create(null);
function reportDiagnostics(d) {
    for (let i = 0; i < d.items.length; i++) {
        const it = d.items[i];
        if (it.level !== 'bad' && it.level !== 'warn') continue;
        if (_diagSent[it.key]) continue;
        _diagSent[it.key] = 1;
        if (window.__aeDiagnostic) {
            try {
                window.__aeDiagnostic({
                    issue: it.key, level: it.level, browser: env.browser, os: env.os,
                    pen: observed.penSeen ? 1 : 0, analog: observed.analogPressure ? 1 : 0
                });
            } catch (_) {}
        }
    }
}
let completeSent = false;
let completeTimer = null;
/**
 * `complete` шлём по УСТОЯВШЕМУСЯ вердикту, а не по первому попавшемуся.
 * Первый вердикт часто оптимистичен: после одного штриха всё «ok», а через
 * три секунды вылезают порог и дрожание, и итог становится «partial».
 * Отправив ранний, мы бы систематически завышали долю исправных перьев —
 * то есть врали бы сами себе именно в той цифре, ради которой ставили счётчик.
 */
function scheduleComplete() {
    if (completeSent) return;
    if (completeTimer) clearTimeout(completeTimer);
    completeTimer = setTimeout(() => {
        const d = currentDiagnosis();
        if (d.status !== 'unknown') reportComplete(d, measurements());
    }, 4000);
}

/**
 * Итоговый вердикт при уходе со страницы.
 *
 * `complete` по паузе всё равно может застать промежуточное состояние: человек
 * сделал штрих, задумался, потом прижал перо — и «ok» стало «partial». Поэтому
 * при уходе отправляем ПОСЛЕДНЕЕ значение отдельным событием; примитив
 * телеметрии на pagehide отправляет очередь через sendBeacon, так что оно
 * доезжает и при закрытии вкладки.
 */
let finalSent = false;
function reportFinalVerdict() {
    if (finalSent) return;
    finalSent = true;
    const d = currentDiagnosis();
    if (d.status === 'unknown' || !window.__ae) return;
    const m = measurements();
    try {
        window.__ae('tool_verdict_final', {
            slug: SLUG, verdict: d.status,
            pointer: observed.penSeen ? 'pen' : (observed.touchSeen ? 'touch' : 'mouse'),
            issues: d.items.filter(i => i.level === 'bad' || i.level === 'warn').map(i => i.key).join(','),
            levels: m.levels && m.levels.confidence !== 'low' && m.levels.confidence !== 'none'
                ? (m.levels.snapped || m.levels.levels) : null,
            hz: m.rate ? m.rate.hz : null,
            samples: m.penSamples,
            browser: env.browser, os: env.os
        });
    } catch (_) {}
}
window.addEventListener('pagehide', reportFinalVerdict);
document.addEventListener('visibilitychange', () => { if (document.hidden) reportFinalVerdict(); });
function reportComplete(d, m) {
    if (completeSent || d.status === 'unknown') return;
    completeSent = true;
    if (window.__aeToolComplete) {
        try {
            window.__aeToolComplete({
                ok: d.status !== 'broken',
                verdict: d.status,
                pointer: observed.penSeen ? 'pen' : (observed.touchSeen ? 'touch' : 'mouse'),
                analog: observed.analogPressure ? 1 : 0,
                levels: m.levels && m.levels.confidence !== 'low' && m.levels.confidence !== 'none'
                    ? (m.levels.snapped || m.levels.levels) : null,
                hz: m.rate ? m.rate.hz : null,
                tilt: observed.tiltSeen ? 1 : 0,
                samples: m.penSamples,
                browser: env.browser, os: env.os
            });
        } catch (_) {}
    }
}

/* ───────────────────────── холсты ───────────────────────── */

/**
 * Подгонка холста под CSS-размер с учётом DPR.
 *
 * ⚠️ Скрытая панель (display:none) даёт нулевой прямоугольник, а присвоение
 * `canvas.width` — даже тем же числом — ОЧИЩАЕТ холст. Из-за этого любое
 * переключение вкладки стирало нарисованное: человек рисует, уходит посмотреть
 * отчёт, возвращается — пусто. Поэтому на нулевом размере не трогаем ничего
 * и говорим вызывающему, что рисовать сейчас некуда.
 */
function fitCanvas(cv) {
    const r = cv.getBoundingClientRect();
    const ctx = cv.getContext('2d');
    if (r.width < 2 || r.height < 2) return { ctx: ctx, w: 0, h: 0, dpr: 1, hidden: true };
    const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    const w = Math.max(1, Math.round(r.width * dpr));
    const h = Math.max(1, Math.round(r.height * dpr));
    if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { ctx: ctx, w: r.width, h: r.height, dpr: dpr, hidden: false };
}

function themeColors() {
    const light = document.documentElement.getAttribute('data-theme') === 'light';
    return {
        ink: light ? '#0f172a' : '#e2e8f0',
        accent: '#38bdf8',
        grid: light ? 'rgba(0,0,0,.10)' : 'rgba(255,255,255,.10)',
        muted: light ? 'rgba(15,23,42,.45)' : 'rgba(226,232,240,.45)',
        ok: '#22c55e', warn: '#f59e0b', bad: '#ef4444'
    };
}

/* ───────────────────── вкладки ───────────────────── */

const tabs = Array.prototype.slice.call(document.querySelectorAll('.stl-tab'));
let activeTab = 'live';
tabs.forEach(btn => {
    btn.addEventListener('click', () => {
        activeTab = btn.dataset.tab;
        tabs.forEach(b => {
            const on = b === btn;
            b.classList.toggle('active', on);
            b.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        document.querySelectorAll('.stl-panel').forEach(p => {
            p.classList.toggle('active', p.id === 'pane-' + activeTab);
        });
        stageRepeat('tab_' + activeTab);
        onResize();
        if (activeTab === 'report') renderReport();
        if (activeTab === 'line') drawLineGuide();
        if (activeTab === 'area') drawArea();
        if (activeTab === 'pressure') { drawRamp(); drawHist(); }
    });
});

/* ───────────────── живая зона рисования ───────────────── */

const padCv = $('stlPadCanvas');
let padCtx = null, padW = 0, padH = 0;

/**
 * Живая зона переживает изменение размера.
 *
 * Смена размера холста его очищает — а размер меняется не только при повороте
 * телефона, но и при «во весь экран», и при обычном ресайзе окна. Без переноса
 * нарисованного человек, развернувший зону, чтобы дорисовать крупнее, каждый
 * раз получал бы чистый лист. Переносим старое изображение, растянув по новой
 * геометрии: точность штриха тут не важна, важно не потерять работу.
 */
function initPad() {
    const before = (padCv.width > 1 && padCv.height > 1) ? padCv : null;
    let snap = null;
    if (before) {
        try {
            snap = document.createElement('canvas');
            snap.width = padCv.width; snap.height = padCv.height;
            snap.getContext('2d').drawImage(padCv, 0, 0);
        } catch (_) { snap = null; }
    }
    const prevW = padCv.width, prevH = padCv.height;
    const f = fitCanvas(padCv);
    if (f.hidden) return;
    padCtx = f.ctx; padW = f.w; padH = f.h;
    if (snap && (prevW !== padCv.width || prevH !== padCv.height)) {
        try {
            padCtx.save();
            padCtx.setTransform(1, 0, 0, 1, 0, 0);
            padCtx.drawImage(snap, 0, 0, prevW, prevH, 0, 0, padCv.width, padCv.height);
            padCtx.restore();
        } catch (_) {}
    }
}

/** Толщина штриха от нажима. Мышь (0.5) даёт ровную линию, перо — переменную. */
function strokeWidth(p, type) {
    if (type !== 'pen') return 2.2;
    return 0.8 + Math.pow(Math.max(0, Math.min(1, p)), 0.75) * 20;
}

function drawSegment(ctx, a, b) {
    if (!ctx) return;
    const c = themeColors();
    const w = strokeWidth(b.p, b.type);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = b.type === 'pen' ? (b.b & 32 ? '#f472b6' : c.accent) : c.muted;
    ctx.lineWidth = w;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
    // Наклон рисуем «тенью» от кончика — так видно, что данные о наклоне живые.
    if (b.type === 'pen' && (Math.abs(b.tx) > 1 || Math.abs(b.ty) > 1)) {
        ctx.strokeStyle = 'rgba(56,189,248,.22)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(b.x, b.y);
        ctx.lineTo(b.x + b.tx * 0.55, b.y + b.ty * 0.55);
        ctx.stroke();
    }
}

/**
 * След пальца. Рисуем НАМЕРЕННО иначе, чем перо: тусклое полупрозрачное пятно
 * без переменной толщины. Так человек сразу видит, что касание принято, но это
 * не перо, — вместо мёртвой зоны, из которой непонятно, работает ли страница.
 */
function drawTouchMark(ctx, s) {
    if (!ctx) return;
    ctx.save();
    ctx.fillStyle = 'rgba(148,163,184,.35)';
    ctx.beginPath();
    ctx.arc(s.x, s.y, Math.max(8, Math.min(26, (s.w || 16) / 2)), 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
}

/* ───────────── непрерывные измерения ───────────── */

const state = {
    bestRamp: null,          // самый длинный внятный разгон нажима
    hold: null,              // дрожание при удержании
    holdBuf: [],
    holdAnchor: null,
    lastMoveT: 0,
    hzWindow: [],            // мгновенная частота для графика
    hzSeries: [],
    tiltMaxX: 0, tiltMaxY: 0, twistMin: null, twistMax: null, tangMax: 0,
    tiltQuadrants: new Set(),
    lineBest: null,
    areaCells: null,
    areaCols: 16, areaRows: 9,
    baseline: null
};

let penSeenApplied = false;
/** Перо приехало — только теперь можно глушить прокрутку на зонах (см. CSS). */
function applyPenSeen() {
    if (penSeenApplied) return;
    penSeenApplied = true;
    document.querySelectorAll('.stl-pad, .stl-grid-wrap').forEach(el => el.classList.add('pen-seen'));
    stage('pen_detected');
}

function noteSample(s) {
    if (s.type !== 'pen') return;
    applyPenSeen();
    if (Math.abs(s.tx) > state.tiltMaxX) state.tiltMaxX = Math.round(Math.abs(s.tx));
    if (Math.abs(s.ty) > state.tiltMaxY) state.tiltMaxY = Math.round(Math.abs(s.ty));
    if (s.tw) {
        if (state.twistMin === null || s.tw < state.twistMin) state.twistMin = s.tw;
        if (state.twistMax === null || s.tw > state.twistMax) state.twistMax = s.tw;
    }
    if (s.tp > state.tangMax) state.tangMax = s.tp;
    if (Math.abs(s.tx) > 4 || Math.abs(s.ty) > 4) {
        state.tiltQuadrants.add((s.tx >= 0 ? 'R' : 'L') + (s.ty >= 0 ? 'D' : 'U'));
    }

    // Мгновенная частота для графика (по коалесцированным сэмплам).
    if (state.lastMoveT && s.t > state.lastMoveT) {
        const dt = s.t - state.lastMoveT;
        if (dt > 0 && dt < 60) {
            state.hzWindow.push(1000 / dt);
            if (state.hzWindow.length > 40) state.hzWindow.shift();
            if (state.hzWindow.length >= 8) {
                state.hzSeries.push(median(state.hzWindow));
                if (state.hzSeries.length > 600) state.hzSeries.shift();
            }
        }
    }
    state.lastMoveT = s.t;

    // Удержание на месте: копим нажим, пока перо стоит.
    if (s.p > 0.03) {
        if (!state.holdAnchor || Math.hypot(s.x - state.holdAnchor.x, s.y - state.holdAnchor.y) > 4) {
            state.holdAnchor = { x: s.x, y: s.y, t: s.t };
            state.holdBuf.length = 0;
        }
        state.holdBuf.push(s.p);
        if (s.t - state.holdAnchor.t > 1200 && state.holdBuf.length >= 40) {
            const h = holdJitter(state.holdBuf);
            // Оставляем самый длинный удержанный участок — он честнее короткого.
            if (h && (!state.hold || h.n > state.hold.n)) state.hold = h;
        }
    } else {
        state.holdAnchor = null;
        state.holdBuf.length = 0;
    }
}

function noteStrokeEnd(stroke) {
    if (!stroke || stroke.length < 12) return;
    if (stroke[0].type !== 'pen') return;
    const r = pressureRamp(stroke);
    if (r && r.peak > 0.5 && r.durMs > 250) {
        if (!state.bestRamp || r.durMs > state.bestRamp.durMs) state.bestRamp = r;
    }
}

/* ───────────── живая панель ───────────── */

const fType = $('fType'), fPressure = $('fPressure'), fTiltX = $('fTiltX'), fTiltY = $('fTiltY');
const fTwist = $('fTwist'), fTang = $('fTang'), fButtons = $('fButtons'), fRate = $('fRate');
const fCoords = $('fCoords'), fContact = $('fContact'), fLatency = $('fLatency'), fSamples = $('fSamples');
const pressBar = $('stlPressBar'), pressBar2 = $('stlPressBar2');

let liveSample = null;
let liveDirty = false;

function paintLive() {
    if (!liveDirty) return;
    liveDirty = false;
    const s = liveSample;
    if (s) {
        fType.textContent = s.type;
        fPressure.textContent = s.p.toFixed(3);
        fTiltX.textContent = Math.round(s.tx) + '°';
        fTiltY.textContent = Math.round(s.ty) + '°';
        fTwist.textContent = caps.twistField ? Math.round(s.tw) + '°' : '—';
        fTang.textContent = caps.tangentialField ? s.tp.toFixed(2) : '—';
        fButtons.textContent = buttonsLabel(s.b);
        fCoords.textContent = Math.round(s.x) + ', ' + Math.round(s.y);
        fContact.textContent = (s.w || s.h) ? (Math.round(s.w) + '×' + Math.round(s.h)) : '—';
        const w = Math.max(0, Math.min(1, s.p)) * 100;
        pressBar.style.width = w + '%';
        if (pressBar2) pressBar2.style.width = w + '%';
    }
    const hz = state.hzWindow.length >= 8 ? Math.round(median(state.hzWindow)) : null;
    fRate.textContent = hz ? hz + ' Hz' : '—';
    const lat = latency(session.latencies);
    fLatency.textContent = lat ? Math.round(lat.median) + ' ms' : '—';
    fSamples.textContent = session.samples.length;
}

function buttonsLabel(b) {
    if (!b) return '—';
    const out = [];
    if (b & 1) out.push(T.btn_tip || 'tip');
    if (b & 2) out.push(T.btn_barrel || 'barrel');
    if (b & 4) out.push('mid');
    if (b & 32) out.push(T.btn_eraser || 'eraser');
    return out.length ? out.join('+') : String(b);
}

/* ───────────── баннер-вердикт ───────────── */

const banner = $('stlBanner'), bIcon = $('stlBannerIcon'), bTitle = $('stlBannerTitle'), bSub = $('stlBannerSub');

function updateBanner() {
    const d = currentDiagnosis();
    let cls = '', icon = '🖊️', title = T.banner_idle_title || '', sub = T.banner_idle_sub || '';
    if (!observed.penSeen && observed.touchSeen && !observed.mouseSeen) {
        // Телефон. «Перо работает не так, как должно» здесь читается как
        // поломка, хотя человек просто ткнул пальцем в устройство, у которого
        // активного пера нет и не предполагалось.
        cls = 'warn'; icon = '👆';
        title = T.banner_touch_title || T.banner_bad_title;
        sub = T.banner_touch_sub || T.banner_bad_sub;
    }
    else if (!observed.penSeen && observed.mouseSeen) {
        // Отдельное состояние: перо ни разу не приезжало, зато ездит мышь.
        // Общий текст «перо работает частично» здесь врал бы человеку, который
        // просто зашёл посмотреть и планшета не имеет вовсе.
        cls = 'warn'; icon = '🖱️';
        title = T.banner_mouse_title || T.banner_partial_title;
        sub = T.banner_mouse_sub || T.banner_partial_sub;
    }
    else if (d.status === 'ok') { cls = 'ok'; icon = '✅'; title = T.banner_ok_title; sub = T.banner_ok_sub; }
    else if (d.status === 'partial') { cls = 'warn'; icon = '⚠️'; title = T.banner_partial_title; sub = T.banner_partial_sub; }
    else if (d.status === 'broken') { cls = 'bad'; icon = '⛔'; title = T.banner_bad_title; sub = firstProblemText(d) || T.banner_bad_sub; }
    banner.className = 'stl-banner' + (cls ? ' ' + cls : '');
    bIcon.textContent = icon;
    bTitle.textContent = title || '';
    bSub.textContent = sub || '';

    // Точки на вкладках: где смотреть дальше.
    setDot('pressure', observed.penSeen ? (observed.analogPressure ? 'ok' : 'bad') : '');
    setDot('tilt', observed.penSeen ? (observed.tiltSeen ? 'ok' : 'warn') : '');
    setDot('line', state.lineBest ? 'ok' : '');
    setDot('area', state.areaCells ? (coverage() > 0.85 ? 'ok' : 'warn') : '');
    setDot('report', d.status === 'ok' ? 'ok' : (d.status === 'broken' ? 'bad' : (d.status === 'partial' ? 'warn' : '')));
}

function setDot(name, cls) {
    const el = document.querySelector('[data-dot="' + name + '"]');
    if (el) el.className = 'stl-tab-dot' + (cls ? ' ' + cls : '');
}

function firstProblemText(d) {
    for (let i = 0; i < d.items.length; i++) {
        if (d.items[i].level === 'bad') {
            const e = DIAG[d.items[i].key];
            if (e && e.t) return e.t;
        }
    }
    return null;
}

/* ───────────── сводка измерений ───────────── */

function measurements() {
    const penSamples = session.samples.filter(s => s.type === 'pen');
    const pressures = penSamples.map(s => s.p).filter(p => p > 0);
    const distinct = new Set(pressures.map(p => Math.round(p * 1e6))).size;
    const penStrokes = session.strokes.filter(st => st.length && st[0].type === 'pen');
    return {
        caps: caps,
        observed: observed,
        env: env,
        penSamples: penSamples.length,
        levels: effectivePressureLevels(pressures),
        activation: activationForce(penStrokes),
        rate: reportRate(penSamples),
        lat: latency(session.latencies),
        hold: state.hold,
        ramp: state.bestRamp,
        spikes: pressureSpikes(penStrokes),
        wob: state.lineBest,
        maxPressure: pressures.length ? Math.max.apply(null, pressures) : null,
        minPressure: pressures.length ? Math.min.apply(null, pressures) : null,
        distinctPressures: distinct,
        tiltMaxX: state.tiltMaxX,
        tiltMaxY: state.tiltMaxY,
        coverage: coverage()
    };
}

let diagCache = null, diagCacheAt = 0;
function currentDiagnosis() {
    const now = performance.now();
    if (diagCache && now - diagCacheAt < 400) return diagCache;
    diagCache = diagnose(measurements());
    diagCacheAt = now;
    return diagCache;
}

/* ───────────── подписка зон ───────────── */

function bindPad(hostId, canvasId, hintId, extra) {
    const host = $(hostId);
    if (!host) return null;
    const cv = canvasId ? $(canvasId) : null;
    const hint = hintId ? $(hintId) : null;
    let ctx = null, last = null;

    function ensure() { if (cv) { const f = fitCanvas(cv); if (!f.hidden) ctx = f.ctx; } }
    ensure();

    attach(host, {
        onDown(s) {
            if (hint) hint.classList.add('hidden');
            last = s;
            liveSample = s; liveDirty = true;
            noteSample(s);
            if (extra && extra.onDown) extra.onDown(s, ctx);
        },
        onMove(s) {
            noteSample(s);
            if (ctx && last) drawSegment(ctx, last, s);
            last = s;
            liveSample = s; liveDirty = true;
            if (extra && extra.onMove) extra.onMove(s, ctx);
        },
        onHover(s) {
            liveSample = s; liveDirty = true;
            if (hint && s.type === 'pen') hint.classList.add('hidden');
            if (extra && extra.onHover) extra.onHover(s, ctx);
        },
        onUp(stroke) {
            last = null;
            noteStrokeEnd(stroke);
            if (extra && extra.onUp) extra.onUp(stroke, ctx);
            refreshAll();
        },
        onTouch(s) {
            // ⚠️ Без этого телефон был мёртвым: палец не рисует (он улика для
            // теста ладони, а не инструмент), обработчика onUp у него нет, и
            // refreshAll не вызывался НИКОГДА. Человек тыкал в зону, а страница
            // продолжала показывать «проведите пером» — тул выглядел сломанным.
            if (hint) hint.classList.add('hidden');
            drawTouchMark(ctx, s);
            if (extra && extra.onTouch) extra.onTouch(s);
            refreshAll();
        }
    });
    return { host: host, canvas: cv, redraw: ensure };
}

/* ───────────── вкладка «Нажим» ───────────── */

function drawRamp() {
    const cv = $('stlRampChart'); if (!cv) return;
    const f = fitCanvas(cv); if (f.hidden) return;
    const ctx = f.ctx, c = themeColors();
    ctx.clearRect(0, 0, f.w, f.h);
    const padL = 30, padB = 18, padT = 8, padR = 8;
    const plotW = f.w - padL - padR, plotH = f.h - padT - padB;

    ctx.strokeStyle = c.grid; ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
        const y = padT + plotH * i / 4;
        ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(padL + plotW, y); ctx.stroke();
        ctx.fillStyle = c.muted; ctx.font = '10px system-ui,sans-serif'; ctx.textAlign = 'end';
        ctx.fillText((100 - i * 25) + '%', padL - 4, y + 3);
    }
    const r = state.bestRamp;
    if (!r || !r.curve.length) {
        ctx.fillStyle = c.muted; ctx.font = '12px system-ui,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText(T.awaiting || '', padL + plotW / 2, padT + plotH / 2);
        return;
    }
    const dur = Math.max(1, r.curve[r.curve.length - 1].t);
    ctx.strokeStyle = c.accent; ctx.lineWidth = 2; ctx.beginPath();
    r.curve.forEach((pt, i) => {
        const x = padL + plotW * (pt.t / dur);
        const y = padT + plotH * (1 - Math.max(0, Math.min(1, pt.p)));
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.stroke();
    // Потолок: если перо долго стоит на 1.0 — это клиппинг, подсвечиваем.
    if (r.ceilShare > 0.05) {
        ctx.strokeStyle = 'rgba(245,158,11,.7)'; ctx.setLineDash([4, 4]); ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(padL, padT); ctx.lineTo(padL + plotW, padT); ctx.stroke();
        ctx.setLineDash([]);
    }
    ctx.fillStyle = c.muted; ctx.font = '10px system-ui,sans-serif'; ctx.textAlign = 'center';
    ctx.fillText(Math.round(dur) + ' ms', padL + plotW / 2, f.h - 5);
}

function drawHist() {
    const cv = $('stlHistChart'); if (!cv) return;
    const f = fitCanvas(cv); if (f.hidden) return;
    const ctx = f.ctx, c = themeColors();
    ctx.clearRect(0, 0, f.w, f.h);
    const BINS = 48, padL = 6, padR = 6, padB = 16, padT = 6;
    const plotW = f.w - padL - padR, plotH = f.h - padT - padB;
    const bins = new Array(BINS).fill(0);
    let n = 0;
    for (let i = 0; i < session.samples.length; i++) {
        const s = session.samples[i];
        if (s.type !== 'pen' || s.p <= 0) continue;
        const b = Math.min(BINS - 1, Math.floor(s.p * BINS));
        bins[b]++; n++;
    }
    if (!n) {
        ctx.fillStyle = c.muted; ctx.font = '12px system-ui,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText(T.awaiting || '', f.w / 2, f.h / 2);
        return;
    }
    const max = Math.max.apply(null, bins);
    const bw = plotW / BINS;
    for (let i = 0; i < BINS; i++) {
        const h = plotH * (bins[i] / max);
        ctx.fillStyle = bins[i] ? c.accent : 'transparent';
        ctx.globalAlpha = 0.85;
        ctx.fillRect(padL + i * bw, padT + plotH - h, Math.max(1, bw - 1), h);
    }
    ctx.globalAlpha = 1;
    ctx.fillStyle = c.muted; ctx.font = '10px system-ui,sans-serif';
    ctx.textAlign = 'start'; ctx.fillText('0%', padL, f.h - 4);
    ctx.textAlign = 'end'; ctx.fillText('100%', f.w - padR, f.h - 4);
}

function refreshPressurePane() {
    const m = measurements();
    const lv = m.levels;
    setRes('rLevels', lv && lv.levels && lv.confidence !== 'none' && lv.confidence !== 'low'
        ? '≈' + (lv.snapped || lv.levels)
        : (lv && lv.distinct ? '?' : '—'),
        lv && lv.snapped && lv.snapped <= 128 ? 'warn' : (lv && lv.levels ? 'ok' : ''));

    const act = m.activation;
    setRes('rActivation', act && act.minPositive !== null ? (act.minPositive * 100).toFixed(1) + '%' : '—',
        act && act.minPositive !== null ? (act.minPositive > 0.12 ? 'warn' : 'ok') : '');

    setRes('rPeak', m.maxPressure !== null ? Math.round(m.maxPressure * 100) + '%' : '—',
        m.maxPressure === null ? '' : (m.maxPressure >= 0.92 ? 'ok' : 'warn'));

    setRes('rJitter', m.hold ? (m.hold.sigma * 100).toFixed(2) + '%' : '—',
        m.hold ? (m.hold.sigma > 0.02 ? 'warn' : 'ok') : '');

    // Карточка показывает ВЫБРОСЫ, а не прежнюю «ровность роста»: та красила
    // исправное перо в живой руке жёлтым (замер на стенде), то есть врала
    // человеку цветом ещё до всякого вердикта.
    setRes('rMono', m.spikes ? (m.spikes.rate * 100).toFixed(1) + '%' : '—',
        m.spikes ? (m.spikes.rate > 0.01 ? 'warn' : 'ok') : '');

    setRes('rCeil', m.ramp ? Math.round(m.ramp.ceilShare * 100) + '%' : '—',
        m.ramp ? (m.ramp.ceilShare > 0.35 ? 'warn' : 'ok') : '');

    // Шаги: зажигаем по фактическому наличию данных, а не по кнопкам.
    markStep(0, !!m.ramp);
    markStep(1, !!(act && act.minPositive !== null && act.strokes >= 2));
    markStep(2, !!m.hold);

    const note = $('stlLevelsNote');
    if (note) {
        if (!lv || !lv.levels || lv.confidence === 'none') note.textContent = T.levels_need_more || '';
        else if (lv.confidence === 'low') note.textContent = (T.levels_low_conf || '').replace(':n', lv.distinct);
        else {
            let s = (T.levels_measured || '').replace(':n', String(lv.snapped || lv.levels));
            if ((lv.snapped || lv.levels) <= 1100 && (lv.snapped || lv.levels) >= 900 && env.os === 'Windows') {
                s += ' ' + (T.levels_ink_note || '');
            }
            note.textContent = s;
        }
    }
    if (activeTab === 'pressure') { drawRamp(); drawHist(); }
}

function markStep(i, done) {
    const el = document.querySelector('.stl-step[data-step="' + i + '"]');
    if (el) el.classList.toggle('done', !!done);
}

function setRes(id, text, cls) {
    const el = $(id); if (!el) return;
    el.textContent = text;
    const item = el.closest('.stl-res-item');
    if (item) item.className = 'stl-res-item' + (cls ? ' ' + cls : '');
}

/* ───────────── вкладка «Наклон» ───────────── */

function drawTilt(s) {
    const cv = $('stlTiltCanvas'); if (!cv) return;
    const f = fitCanvas(cv); if (f.hidden) return;
    const ctx = f.ctx, c = themeColors();
    ctx.clearRect(0, 0, f.w, f.h);
    const cx = f.w / 2, cy = f.h / 2;

    // Круг-«горизонт» и деления наклона
    ctx.strokeStyle = c.grid; ctx.lineWidth = 1;
    const R = Math.min(f.w, f.h) * 0.38;
    [0.33, 0.66, 1].forEach(k => {
        ctx.beginPath(); ctx.arc(cx, cy, R * k, 0, Math.PI * 2); ctx.stroke();
    });
    ctx.beginPath(); ctx.moveTo(cx - R, cy); ctx.lineTo(cx + R, cy);
    ctx.moveTo(cx, cy - R); ctx.lineTo(cx, cy + R); ctx.stroke();
    ctx.fillStyle = c.muted; ctx.font = '10px system-ui,sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('30°', cx + R * 0.33, cy - 4);
    ctx.fillText('60°', cx + R * 0.66, cy - 4);
    ctx.fillText('90°', cx + R, cy - 4);

    if (!s || s.type !== 'pen') {
        ctx.fillStyle = c.muted; ctx.font = '13px system-ui,sans-serif';
        ctx.fillText(T.tilt_awaiting || '', cx, cy + R + 22);
        return;
    }
    // Перо: проекция оси. tiltX/tiltY — углы от вертикали в плоскостях XZ/YZ.
    const kx = Math.max(-1, Math.min(1, s.tx / 90));
    const ky = Math.max(-1, Math.min(1, s.ty / 90));
    const px = cx + kx * R, py = cy + ky * R;

    ctx.strokeStyle = c.accent; ctx.lineWidth = 6; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(px, py); ctx.stroke();
    ctx.fillStyle = c.accent;
    ctx.beginPath(); ctx.arc(cx, cy, 5, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = c.ink;
    ctx.beginPath(); ctx.arc(px, py, 8, 0, Math.PI * 2); ctx.fill();

    // Поворот пера — риска на «шляпке»
    if (s.tw) {
        const a = s.tw * Math.PI / 180;
        ctx.strokeStyle = '#f472b6'; ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(px - Math.cos(a) * 14, py - Math.sin(a) * 14);
        ctx.lineTo(px + Math.cos(a) * 14, py + Math.sin(a) * 14);
        ctx.stroke();
    }
}

function refreshTiltPane() {
    setRes('rTiltMax', (state.tiltMaxX || state.tiltMaxY) ? state.tiltMaxX + '° / ' + state.tiltMaxY + '°' : '—',
        (state.tiltMaxX || state.tiltMaxY) ? 'ok' : '');
    const s = liveSample;
    if (s && s.type === 'pen') {
        const sph = (s.alt !== null && s.alt !== undefined)
            ? { altitudeDeg: s.alt * 180 / Math.PI, azimuthDeg: (s.azi || 0) * 180 / Math.PI }
            : tiltToSpherical(s.tx, s.ty);
        setRes('rAltitude', isFinite(sph.altitudeDeg) ? Math.round(sph.altitudeDeg) + '°' : '—', '');
        setRes('rAzimuth', isFinite(sph.azimuthDeg) ? Math.round(sph.azimuthDeg) + '°' : '—', '');
    }
    setRes('rTwistRange', state.twistMax !== null ? Math.round(state.twistMin) + '…' + Math.round(state.twistMax) + '°' : '—',
        state.twistMax !== null ? 'ok' : '');
    setRes('rTangMax', state.tangMax > 0 ? Math.round(state.tangMax * 100) + '%' : '—', state.tangMax > 0 ? 'ok' : '');
    setRes('rTiltCover', state.tiltQuadrants.size + '/4', state.tiltQuadrants.size === 4 ? 'ok' : '');
}

/* ───────────── вкладка «Линия» ───────────── */

let lineGuide = null;

function drawLineGuide() {
    const cv = $('stlLineCanvas'); if (!cv) return;
    const f = fitCanvas(cv); if (f.hidden) return;
    const ctx = f.ctx, c = themeColors();
    ctx.clearRect(0, 0, f.w, f.h);
    // Диагональ: именно на медленной диагонали вылезает волнистость дешёвых
    // планшетов — на горизонтали её маскирует интерполяция драйвера.
    lineGuide = { x1: f.w * 0.1, y1: f.h * 0.78, x2: f.w * 0.9, y2: f.h * 0.22 };
    ctx.strokeStyle = c.grid; ctx.lineWidth = 10; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(lineGuide.x1, lineGuide.y1); ctx.lineTo(lineGuide.x2, lineGuide.y2); ctx.stroke();
    ctx.strokeStyle = c.muted; ctx.lineWidth = 1; ctx.setLineDash([5, 5]);
    ctx.beginPath(); ctx.moveTo(lineGuide.x1, lineGuide.y1); ctx.lineTo(lineGuide.x2, lineGuide.y2); ctx.stroke();
    ctx.setLineDash([]);
    if (state.lineBest && state.lineBest.pts) {
        ctx.strokeStyle = c.accent; ctx.lineWidth = 2; ctx.beginPath();
        state.lineBest.pts.forEach((p, i) => { if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y); });
        ctx.stroke();
    }
}

function onLineStroke(stroke) {
    if (!stroke || stroke.length < 20 || !lineGuide) return;
    const pts = stroke.filter(s => s.type === 'pen' || s.type === 'mouse').map(s => ({ x: s.x, y: s.y, t: s.t }));
    if (pts.length < 20) return;
    const span = Math.hypot(pts[pts.length - 1].x - pts[0].x, pts[pts.length - 1].y - pts[0].y);
    const guideLen = Math.hypot(lineGuide.x2 - lineGuide.x1, lineGuide.y2 - lineGuide.y1);
    if (span < guideLen * 0.5) return;                 // слишком короткий штрих не судим
    const w = wobble(pts);
    if (!w) return;
    w.pts = pts;
    if (!state.lineBest || pts.length > state.lineBest.n) state.lineBest = w;
    drawLineGuide();
    refreshLinePane();
}

function refreshLinePane() {
    const w = state.lineBest;
    setRes('rWobRms', w ? w.rms.toFixed(2) + ' px' : '—', w ? (w.rms > 6 ? 'warn' : 'ok') : '');
    setRes('rWobHf', w ? w.hf.toFixed(2) + ' px' : '—', w ? (w.hf > 1.5 ? 'warn' : 'ok') : '');
    setRes('rWobSlow', w && w.slowHf !== null ? w.slowHf.toFixed(2) + ' px' : '—',
        w && w.slowHf !== null ? (w.slowHf > 1.5 ? 'warn' : 'ok') : '');
    const r = reportRate(session.samples.filter(s => s.type === 'pen'));
    setRes('rHz', r.hz ? r.hz + ' Hz' : '—', r.hz ? (r.hz < 60 ? 'warn' : 'ok') : '');
    setRes('rHzSigma', r.sigma !== null ? '±' + r.sigma : '—', '');
    const l = latency(session.latencies);
    setRes('rLat', l ? Math.round(l.median) + ' ms' : '—', l ? (l.median > 45 ? 'warn' : 'ok') : '');
    drawHz();
}

function drawHz() {
    const cv = $('stlHzChart'); if (!cv) return;
    const f = fitCanvas(cv); if (f.hidden) return;
    const ctx = f.ctx, c = themeColors();
    ctx.clearRect(0, 0, f.w, f.h);
    const padL = 34, padR = 8, padT = 8, padB = 16;
    const plotW = f.w - padL - padR, plotH = f.h - padT - padB;
    const series = state.hzSeries;
    const maxHz = Math.max(140, Math.ceil((series.length ? Math.max.apply(null, series) : 120) * 1.15 / 40) * 40);
    ctx.strokeStyle = c.grid; ctx.lineWidth = 1;
    ctx.font = '10px system-ui,sans-serif'; ctx.textAlign = 'end';
    for (let i = 0; i <= 4; i++) {
        const y = padT + plotH * i / 4;
        ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(padL + plotW, y); ctx.stroke();
        ctx.fillStyle = c.muted;
        ctx.fillText(Math.round(maxHz * (1 - i / 4)), padL - 4, y + 3);
    }
    if (series.length < 3) {
        ctx.fillStyle = c.muted; ctx.textAlign = 'center'; ctx.font = '12px system-ui,sans-serif';
        ctx.fillText(T.awaiting || '', padL + plotW / 2, padT + plotH / 2);
        return;
    }
    ctx.strokeStyle = c.accent; ctx.lineWidth = 1.6; ctx.beginPath();
    series.forEach((hz, i) => {
        const x = padL + plotW * (i / Math.max(1, series.length - 1));
        const y = padT + plotH * (1 - Math.min(hz, maxHz) / maxHz);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.stroke();
}

/* ───────────── вкладка «Рабочая область» ───────────── */

function ensureAreaCells() {
    if (!state.areaCells) state.areaCells = new Uint8Array(state.areaCols * state.areaRows);
    return state.areaCells;
}

function markArea(s) {
    const cv = $('stlAreaCanvas'); if (!cv) return;
    const r = cv.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const col = Math.floor(Math.max(0, Math.min(0.999, s.x / r.width)) * state.areaCols);
    const row = Math.floor(Math.max(0, Math.min(0.999, s.y / r.height)) * state.areaRows);
    const cells = ensureAreaCells();
    const idx = row * state.areaCols + col;
    if (!cells[idx]) { cells[idx] = 1; drawArea(); refreshAreaPane(); }
}

function coverage() {
    if (!state.areaCells) return 0;
    let n = 0;
    for (let i = 0; i < state.areaCells.length; i++) if (state.areaCells[i]) n++;
    return n / state.areaCells.length;
}

function edgeCoverage() {
    if (!state.areaCells) return null;
    let tot = 0, hit = 0;
    for (let r = 0; r < state.areaRows; r++) {
        for (let c = 0; c < state.areaCols; c++) {
            const edge = (r === 0 || c === 0 || r === state.areaRows - 1 || c === state.areaCols - 1);
            if (!edge) continue;
            tot++;
            if (state.areaCells[r * state.areaCols + c]) hit++;
        }
    }
    return tot ? hit / tot : null;
}

function drawArea() {
    const cv = $('stlAreaCanvas'); if (!cv) return;
    const f = fitCanvas(cv); if (f.hidden) return;
    const ctx = f.ctx, c = themeColors();
    ctx.clearRect(0, 0, f.w, f.h);
    const cells = ensureAreaCells();
    const cw = f.w / state.areaCols, ch = f.h / state.areaRows;
    for (let r = 0; r < state.areaRows; r++) {
        for (let col = 0; col < state.areaCols; col++) {
            const on = cells[r * state.areaCols + col];
            ctx.fillStyle = on ? 'rgba(34,197,94,.35)' : 'rgba(255,255,255,.03)';
            ctx.fillRect(col * cw + 1, r * ch + 1, cw - 2, ch - 2);
        }
    }
    ctx.strokeStyle = c.grid; ctx.lineWidth = 1;
    for (let col = 1; col < state.areaCols; col++) { ctx.beginPath(); ctx.moveTo(col * cw, 0); ctx.lineTo(col * cw, f.h); ctx.stroke(); }
    for (let r = 1; r < state.areaRows; r++) { ctx.beginPath(); ctx.moveTo(0, r * ch); ctx.lineTo(f.w, r * ch); ctx.stroke(); }
    if (coverage() < 0.02) {
        ctx.fillStyle = c.muted; ctx.font = '13px system-ui,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText(T.area_awaiting || '', f.w / 2, f.h / 2);
    }
}

function refreshAreaPane() {
    setRes('rCover', Math.round(coverage() * 100) + '%', coverage() > 0.85 ? 'ok' : (coverage() > 0.4 ? 'warn' : ''));
    const e = edgeCoverage();
    setRes('rEdges', e === null ? '—' : Math.round(e * 100) + '%', e === null ? '' : (e > 0.9 ? 'ok' : (e > 0.5 ? 'warn' : 'bad')));
    setRes('rPalm', observed.palmWithPen ? String(observed.palmWithPen) : (observed.penSeen ? '0' : '—'),
        observed.palmWithPen ? 'warn' : (observed.penSeen ? 'ok' : ''));

    const hoverRow = $('stlHoverRow'), hoverLed = $('stlHoverLed'), hoverTxt = $('stlHoverTxt');
    if (hoverRow) {
        hoverRow.classList.toggle('on', observed.hoverSeen);
        if (hoverLed) hoverLed.textContent = observed.hoverSeen ? '●' : '○';
        if (hoverTxt) hoverTxt.textContent = observed.hoverSeen ? (T.hover_yes || '') : (T.area_hover_wait_js || hoverTxt.textContent);
    }
    const cb = $('capBarrel'), ce = $('capEraser');
    if (cb) cb.classList.toggle('on', observed.barrelSeen);
    if (ce) ce.classList.toggle('on', observed.eraserSeen);
}

/* ───────────── возможности браузера ───────────── */

function renderCaps() {
    const wrap = $('stlCaps'); if (!wrap) return;
    const rows = [
        ['pointer_events', caps.pointerEvents],
        ['coalesced', caps.coalesced],
        ['pressure_analog', observed.analogPressure],
        ['tilt', observed.tiltSeen],
        ['twist', caps.twistField && observed.twistSeen],
        ['tangential', caps.tangentialField && observed.tangentialSeen],
        ['spherical', caps.sphericalField],
        ['raw_update', caps.rawUpdate],
        ['hover', observed.hoverSeen],
        ['contact_geometry', observed.contactGeometrySeen]
    ];
    wrap.innerHTML = '';
    rows.forEach(([key, on]) => {
        const d = document.createElement('div');
        d.className = 'stl-cap ' + (on ? 'on' : 'off');
        d.innerHTML = '<span class="stl-cap-led"></span>';
        d.appendChild(document.createTextNode((T['cap_' + key] || key)));
        wrap.appendChild(d);
    });
    const envEl = $('stlEnv');
    if (envEl) envEl.textContent = env.browser + ' ' + env.version + ' · ' + env.os + ' · DPR ' + env.dpr + ' · ' + env.screen;
}

/* ───────────── отчёт ───────────── */

function verdictLines(d) {
    return d.items.map(it => {
        const e = DIAG[it.key];
        return e && e.t ? e.t : it.key;
    });
}

function fillTemplate(str, params) {
    if (!str) return '';
    return str.replace(/:([a-z_]+)/g, (m, k) => (params && params[k] !== undefined && params[k] !== null) ? params[k] : m);
}

function renderReport() {
    const d = currentDiagnosis();
    const wrap = $('stlVerdicts');
    if (wrap) {
        wrap.innerHTML = '';
        const order = { bad: 0, warn: 1, ok: 2, info: 3 };
        const items = d.items.slice().sort((a, b) => order[a.level] - order[b.level]);
        items.forEach(it => {
            const e = DIAG[it.key] || {};
            const row = document.createElement('div');
            row.className = 'stl-verdict ' + it.level;
            const icon = it.level === 'bad' ? '⛔' : it.level === 'warn' ? '⚠️' : it.level === 'ok' ? '✅' : 'ℹ️';
            const t = document.createElement('div');
            t.className = 'stl-verdict-icon'; t.textContent = icon;
            const body = document.createElement('div');
            const h = document.createElement('div');
            h.className = 'stl-verdict-t'; h.textContent = fillTemplate(e.t || it.key, it.params);
            body.appendChild(h);
            if (e.d) {
                const p = document.createElement('div');
                p.className = 'stl-verdict-d'; p.textContent = fillTemplate(e.d, it.params);
                body.appendChild(p);
            }
            if (e.fix) {
                const fx = document.createElement('div');
                fx.className = 'stl-verdict-fix'; fx.textContent = fillTemplate(e.fix, it.params);
                body.appendChild(fx);
            }
            row.appendChild(t); row.appendChild(body);
            wrap.appendChild(row);
        });
    }
    const raw = $('stlRaw');
    if (raw) raw.value = supportText(measurements(), verdictLines(d));
    renderCompare();
}

/* Сравнение с сохранённым замером: закрывает диагностическую петлю —
   «включил Windows Ink → прогнал ещё раз → вот что изменилось». */
const BASELINE_KEY = 'stylus-test:baseline';

function snapshot() {
    const m = measurements();
    return {
        at: Date.now(),
        levels: m.levels && m.levels.levels ? (m.levels.snapped || m.levels.levels) : null,
        maxPressure: m.maxPressure,
        activation: m.activation ? m.activation.minPositive : null,
        hz: m.rate ? m.rate.hz : null,
        latency: m.lat ? m.lat.median : null,
        slowHf: m.wob ? m.wob.slowHf : null,
        analog: observed.analogPressure,
        tilt: observed.tiltSeen,
        status: currentDiagnosis().status
    };
}

function renderCompare() {
    const el = $('stlCompare'); if (!el) return;
    let base = state.baseline;
    if (!base) {
        try { base = JSON.parse(localStorage.getItem(BASELINE_KEY) || 'null'); } catch (_) { base = null; }
        state.baseline = base;
    }
    if (!base) { el.textContent = T.compare_empty || ''; return; }
    const now = snapshot();
    const rows = [];
    const pair = (label, a, b, unit, better) => {
        if (a === null && b === null) return;
        const fa = a === null ? '—' : (typeof a === 'number' ? round(a) : a);
        const fb = b === null ? '—' : (typeof b === 'number' ? round(b) : b);
        let arrow = '';
        if (typeof a === 'number' && typeof b === 'number' && Math.abs(a - b) > Math.abs(a) * 0.05) {
            const up = b > a;
            arrow = (better === 'up' ? (up ? ' ✅' : ' ⚠️') : (up ? ' ⚠️' : ' ✅'));
        }
        rows.push(label + ': ' + fa + ' → ' + fb + (unit || '') + arrow);
    };
    pair(T.cmp_levels || 'levels', base.levels, now.levels, '', 'up');
    pair(T.cmp_max || 'max', base.maxPressure, now.maxPressure, '', 'up');
    pair(T.cmp_hz || 'Hz', base.hz, now.hz, ' Hz', 'up');
    pair(T.cmp_lat || 'latency', base.latency, now.latency, ' ms', 'down');
    pair(T.cmp_wobble || 'wobble', base.slowHf, now.slowHf, ' px', 'down');
    const when = new Date(base.at).toLocaleString(CFG.locale || 'en');
    el.textContent = (T.compare_since || '').replace(':when', when) + ' ' + rows.join(' · ');
}

function round(v) { return Math.round(v * 1000) / 1000; }

/* ───────────── обновление всего ───────────── */

let refreshQueued = false;
function refreshAll() {
    // Вердикт обновляем СИНХРОННО, а не в rAF.
    //
    // ⚠️ WebKit придерживает requestAnimationFrame агрессивнее Chromium, и на
    // iPhone одиночное касание оставляло баннер в состоянии «проведите пером»
    // при уже зафиксированном касании (замер 2026-08-09: в Chromium то же
    // действие обновляло, в WebKit — нет). Единственная строка, ради которой
    // человек пришёл, не должна ждать кадра; тяжёлое — панели, холсты, отчёт —
    // по-прежнему в rAF.
    diagCache = null;
    updateBanner();

    if (refreshQueued) return;
    refreshQueued = true;
    requestAnimationFrame(() => {
        refreshQueued = false;
        diagCache = null;
        const _d = currentDiagnosis();
        if (observed.penSeen) stage('first_input', { kind: 'pen' });
        else if (observed.touchSeen) stage('first_input', { kind: 'touch' });
        else if (observed.mouseSeen) stage('first_input', { kind: 'mouse' });
        if (observed.analogPressure) stage('pressure_analog');
        if (_d.status !== 'unknown') {
            stage('verdict_' + _d.status);
            reportDiagnostics(_d);
            scheduleComplete();
        }
        updateBanner();
        renderCaps();
        refreshPressurePane();
        refreshTiltPane();
        refreshLinePane();
        refreshAreaPane();
        if (activeTab === 'report') renderReport();
    });
}

/* ───────────── старт ───────────── */

function onResize() {
    initPad();
    ['stlPressPadCanvas', 'stlTiltCanvas'].forEach(id => { const c = $(id); if (c) fitCanvas(c); });
    if (activeTab === 'line') drawLineGuide();
    if (activeTab === 'area') drawArea();
    if (activeTab === 'pressure') { drawRamp(); drawHist(); }
}

let tiltPainted = null;
function loop() {
    // На скрытой вкладке браузер и так тормозит rAF, но не везде: явная пауза
    // экономит батарею телефона, который у этого тула — типичный гость.
    if (!document.hidden) {
        paintLive();
        if (activeTab === 'tilt') {
            // Перерисовываем ТОЛЬКО когда пришёл новый отсчёт: иначе это
            // getBoundingClientRect + полная перерисовка холста 60 раз в секунду
            // на неподвижном пере.
            const sig = liveSample ? (liveSample.t + ':' + liveSample.tx + ':' + liveSample.ty + ':' + liveSample.tw) : 'none';
            if (sig !== tiltPainted) { tiltPainted = sig; drawTilt(liveSample); refreshTiltPane(); }
        }
    }
    requestAnimationFrame(loop);
}

function boot() {
    initPad();
    bindPad('stlPad', 'stlPadCanvas', 'stlPadHint', null);
    bindPad('stlPressPad', 'stlPressPadCanvas', 'stlPressHint', null);
    bindPad('stlTiltPad', null, 'stlTiltHint', null);
    bindPad('stlLinePad', null, 'stlLineHint', {
        onUp(stroke) { onLineStroke(stroke); }
    });
    bindPad('stlAreaPad', null, null, {
        onDown(s) { markArea(s); },
        onMove(s) { markArea(s); }
    });

    $('stlClear').addEventListener('click', () => {
        if (padCtx) padCtx.clearRect(0, 0, padW, padH);
    });
    $('stlReset').addEventListener('click', hardReset);
    $('stlPressReset').addEventListener('click', hardReset);
    $('stlAreaReset').addEventListener('click', () => {
        state.areaCells = null; observed.palmWithPen = 0; drawArea(); refreshAreaPane();
    });
    $('stlPressStart').addEventListener('click', () => {
        state.bestRamp = null; state.hold = null; state.holdBuf.length = 0;
        session.pressures.length = 0;
        refreshPressurePane();
        if (window.__toast) window.__toast(T.press_restarted || '', 'info');
    });
    $('stlLineClear').addEventListener('click', () => { state.lineBest = null; drawLineGuide(); refreshLinePane(); });

    $('stlSaveBaseline').addEventListener('click', () => {
        const snap = snapshot();
        try { localStorage.setItem(BASELINE_KEY, JSON.stringify(snap)); } catch (_) {}
        state.baseline = snap;
        renderCompare();
        if (window.__toast) window.__toast(T.baseline_saved || '', 'success');
        if (window.__ae) window.__ae('feature_use', { slug: SLUG, action: 'baseline_save' });
    });
    $('stlCopy').addEventListener('click', () => {
        const txt = supportText(measurements(), verdictLines(currentDiagnosis()));
        copyText(txt).then(ok => {
            if (window.__toast) window.__toast(ok ? (T.copied || '') : (T.copy_failed || ''), ok ? 'success' : 'error');
        });
        if (window.__ae) window.__ae('feature_use', { slug: SLUG, action: 'copy_support' });
    });
    $('stlJson').addEventListener('click', () => {
        const m = measurements();
        downloadJson({
            tool: 'stylus-test',
            generatedAt: new Date().toISOString(),
            environment: env,
            capabilities: caps,
            observed: {
                pen: observed.penSeen, mouse: observed.mouseSeen, touch: observed.touchSeen,
                barrel: observed.barrelSeen, eraser: observed.eraserSeen, hover: observed.hoverSeen,
                analogPressure: observed.analogPressure, tilt: observed.tiltSeen, twist: observed.twistSeen,
                tangential: observed.tangentialSeen, coalesced: observed.coalescedUsed,
                palmWithPen: observed.palmWithPen
            },
            measurements: {
                samples: m.penSamples,
                pressure: { min: m.minPressure, max: m.maxPressure, distinct: m.distinctPressures, levels: m.levels },
                activation: m.activation, hold: m.hold,
                ramp: m.ramp ? { peak: m.ramp.peak, monotonicity: m.ramp.monotonicity, ceilShare: m.ramp.ceilShare, durMs: m.ramp.durMs } : null,
                rate: m.rate, latency: m.lat,
                wobble: m.wob ? { rms: m.wob.rms, max: m.wob.max, hf: m.wob.hf, slowHf: m.wob.slowHf, n: m.wob.n } : null,
                tilt: { maxX: m.tiltMaxX, maxY: m.tiltMaxY, quadrants: state.tiltQuadrants.size },
                coverage: m.coverage, edgeCoverage: edgeCoverage()
            },
            diagnosis: currentDiagnosis()
        }, 'stylus-test-report.json');
        if (window.__ae) window.__ae('feature_use', { slug: SLUG, action: 'export_json' });
    });
    $('stlPng').addEventListener('click', () => {
        drawReportCard(measurements(), currentDiagnosis(), DIAG, T, env);
        if (window.__ae) window.__ae('feature_use', { slug: SLUG, action: 'export_png' });
        if (window.__aeToolComplete) window.__aeToolComplete({ slug: SLUG, ok: true, kind: 'report_png' });
    });

    // Развернуть зону во весь экран. НЕ Fullscreen API: на iPhone его для
    // произвольного элемента нет (проверено на /ar-drawing), а длинный ход пера
    // нужен именно для теста волнистости — на телефоне зона иначе с ладонь.
    const expandBtn = $('stlExpand');
    if (expandBtn) {
        expandBtn.addEventListener('click', () => {
            const pad = $('stlPad');
            const on = pad.classList.toggle('expanded');
            expandBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
            $('stlExpandTxt').textContent = on ? (T.collapse || '') : (T.expand || '');
            document.body.style.overflow = on ? 'hidden' : '';
            document.body.classList.toggle('stl-expanded', on);
            requestAnimationFrame(() => { initPad(); });
            stageRepeat('expand', { on: on ? 1 : 0 });
        });
    }
    // Esc выходит из развёрнутой зоны — иначе на десктопе из неё некуда деться,
    // если кнопка ушла под перо.
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && $('stlPad').classList.contains('expanded')) expandBtn.click();
    });

    // Клавиатура по вкладкам: стрелки + Home/End, как требует шаблон tablist.
    tabs.forEach((btn, i) => {
        btn.addEventListener('keydown', (e) => {
            const rtl = document.documentElement.getAttribute('dir') === 'rtl';
            let next = null;
            if (e.key === 'ArrowRight') next = rtl ? i - 1 : i + 1;
            else if (e.key === 'ArrowLeft') next = rtl ? i + 1 : i - 1;
            else if (e.key === 'Home') next = 0;
            else if (e.key === 'End') next = tabs.length - 1;
            if (next === null) return;
            e.preventDefault();
            const t = tabs[(next + tabs.length) % tabs.length];
            t.click();
            t.focus();
        });
    });
    // Фокус ходит по активной вкладке, а не по всем шести (шаблон tablist).
    const syncTabIndex = () => tabs.forEach(b => b.tabIndex = b.classList.contains('active') ? 0 : -1);
    tabs.forEach(b => b.addEventListener('click', syncTabIndex));
    syncTabIndex();

    // Баннер сам зовёт в «Отчёт» — значит он обязан туда и вести. Без этого
    // человек читает «смотрите вкладку Отчёт» и ищет её глазами среди шести.
    // Только переключение вкладки, без прокрутки страницы.
    banner.addEventListener('click', () => {
        const d = currentDiagnosis();
        if (d.status === 'unknown') return;
        const t = document.querySelector('.stl-tab[data-tab="report"]');
        if (t) { t.click(); t.focus(); }
    });
    banner.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); banner.click(); }
    });

    window.addEventListener('resize', onResize);
    if (window.matchMedia) {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        if (mq.addEventListener) mq.addEventListener('change', onResize);
    }

    renderCaps();
    refreshAll();
    drawLineGuide();
    drawArea();
    loop();
    stage('open');
}

function hardReset() {
    resetSession();
    state.bestRamp = null; state.hold = null; state.holdBuf.length = 0; state.holdAnchor = null;
    state.hzWindow.length = 0; state.hzSeries.length = 0;
    state.tiltMaxX = state.tiltMaxY = 0; state.twistMin = state.twistMax = null; state.tangMax = 0;
    state.tiltQuadrants.clear();
    state.lineBest = null; state.areaCells = null;
    liveSample = null; liveDirty = true;
    // Сброс должен чистить и тот холст, который сейчас скрыт, — иначе человек
    // нажал «Сбросить» на одной вкладке, а на соседней его прежний штрих цел.
    // Присвоение width очищает холст независимо от видимости.
    ['stlPadCanvas', 'stlPressPadCanvas'].forEach(id => {
        const c = $(id);
        if (c) c.width = c.width;               // eslint-disable-line no-self-assign
    });
    initPad();
    document.querySelectorAll('.stl-pad-hint').forEach(h => h.classList.remove('hidden'));
    document.querySelectorAll('.stl-step').forEach(s => s.classList.remove('done'));
    drawLineGuide(); drawArea();
    refreshAll();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

// Точка входа для e2e: тест дёргает те же функции, что и страница, —
// иначе зелёный прогон доказывал бы работу мока, а не тула.
window.__stylusTest = {
    state: state, session: session, observed: observed, caps: caps,
    measurements: measurements, diagnose: () => currentDiagnosis(),
    snapshot: snapshot, reset: hardReset, env: env
};
